import { createAsyncThunk } from "@reduxjs/toolkit";
import { OrderAndSalesAxios } from "../../api/orderandsalesaxios";
import { API_ENDPOINT } from "../../helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";
import { parse } from "date-fns";
import { convertLocalDateToUTC } from "../../helpers/DateUtils/dateTimezoneUtils";
import { DEFAULT_ORGANISATION } from "pages/QuotationV2/Form/serviceInitialValues";
import { mapAccessoryToApiPayload } from "pages/QuotationV2/Form/Components/ServiceAccessoryRow";

// --- SERVICE QUOTATION THUNKS ---

/**
 * Normalize bill_from so listing always gets organization_name.
 * Empty objects and bare strings are converted to a proper org payload.
 */
const normalizeBillFrom = (billFrom: any) => {
  if (!billFrom) return DEFAULT_ORGANISATION;

  if (typeof billFrom === "string") {
    try {
      const parsed = JSON.parse(billFrom);
      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        Object.keys(parsed).length > 0
      ) {
        return parsed;
      }
    } catch {
      // leave as string address — not usable for organisation_name
    }
    return DEFAULT_ORGANISATION;
  }

  if (
    typeof billFrom === "object" &&
    !Array.isArray(billFrom) &&
    Object.keys(billFrom).length > 0
  ) {
    return billFrom;
  }

  return DEFAULT_ORGANISATION;
};
/**
 * Helper function to convert date string to UTC ISO string
 * Handles yyyy-MMM-dd format (e.g., "2026-Jan-14") and YYYY-MM-DD format
 * Converts local date to UTC ISO string for backend
 */
const convertToUTC = (dateStr: string | undefined): string => {
  if (!dateStr) return "";
  try {
    let localDateStr = "";

    // Try parsing yyyy-MMM-dd format first (e.g., "2026-Jan-14")
    let parsed = parse(dateStr, "yyyy-MMM-dd", new Date());
    if (!isNaN(parsed.getTime())) {
      // Convert to YYYY-MM-DD format
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      localDateStr = `${year}-${month}-${day}`;
    }
    // Try parsing YYYY-MM-DD format (e.g., "2026-01-14")
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      localDateStr = dateStr;
    }
    // Fallback: try parsing as ISO string - if already UTC, return as is
    else if (dateStr.includes("T") || dateStr.includes("Z")) {
      // Already in UTC format, return as is
      return dateStr;
    }

    // Convert local date string to UTC ISO string
    if (localDateStr) {
      return convertLocalDateToUTC(localDateStr);
    }

    return "";
  } catch {
    return "";
  }
};

/**
 * Thunk to fetch a single Service Quotation by its ID.
 * @param id - The UUID of the quotation.
 */
export const downloadServiceQuotationPdf = createAsyncThunk(
  "quotation/downloadServiceQuotationPdf",
  async (
    { id, quotationNumber, withSignature }: { id: string; quotationNumber: string; withSignature?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `${API_ENDPOINT.QUOTATION.SERVICE.DOWNLOAD_PDF(id)}${withSignature !== undefined ? `?withSignature=${withSignature}` : ""}`,
        {
          responseType: "blob", // Important for handling binary file data
        },
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Service_Quotation_${quotationNumber}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          return rejectWithValue(parsed.message || parsed.error || "Failed to download PDF");
        } catch {
          return rejectWithValue("Failed to download PDF");
        }
      }
      return rejectWithValue(error.response?.data?.message || error.response?.data || "Failed to download PDF");
    }
  },
);

/**
 * Thunk to fetch a single Service Quotation by its ID.
 * @param id - The UUID of the quotation.
 */
export const fetchServiceQuotationById = createAsyncThunk(
  "quotation/fetchServiceQuotationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        API_ENDPOINT.QUOTATION.SERVICE.DETAIL(id),
      );
      // The backend returns the object in the format: { status: 200, success: true, message: "...", data: {...} }
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Map form contact_persons to the contacts stored on customer_details.
 */
const mapContactPersonsToCustomerContacts = (contact_persons: any[] = []) =>
  (contact_persons || []).map((contact: any) => ({
    contact_id: contact.id || contact.contact_id || undefined,
    name: contact.name || contact.contact_name || "",
    designation: contact.designation || undefined,
    phone_country: contact.phone_country || undefined,
    phone_number: contact.phone_number || undefined,
    email: contact.email || undefined,
  }));

const buildCustomerDetails = (
  formData: any,
  contact_persons: any[] | undefined,
  company: any,
) => {
  const mappedContacts = mapContactPersonsToCustomerContacts(
    contact_persons || [],
  );

  if (formData.customer_details) {
    const {
      contact_person: _legacyContactPerson,
      contact_person_name: _legacyContactName,
      contact_person_designation: _legacyContactDesignation,
      contact_person_phone_country: _legacyContactPhoneCountry,
      contact_person_phone_no: _legacyContactPhoneNo,
      contact_person_email: _legacyContactEmail,
      ...restCustomerDetails
    } = formData.customer_details;

    return {
      ...restCustomerDetails,
      contacts: mappedContacts,
      contact_details: mappedContacts,
    };
  }

  if (company) {
    return {
      company_id: company.company_id,
      company_name: company.company_name,
      vendor_code: company.vendor_code || "",
      branch_name: company.branch_name || "",
      company_email: company.company_email || "",
      phone_number: company.phone_number || "",
      gstin: company.gstin || "",
      gst_type: company.gst_type || "",
      industry: company.industry || "",
      billing_address: company.billing_address || {},
      shipping_addresses: company.shipping_addresses || [],
      contacts: mappedContacts,
      contact_details: mappedContacts,
    };
  }

  return undefined;
};

/**
 * Transform service quotation frontend data to backend format.
 */
const transformServiceQuotationData = (
  formData: any,
  quotationStatus: string = "Prepared",
  quotationStage: string = "Draft",
) => {
  const {
    quotation_date,
    valid_until,
    enquiry_reference_number,
    enquiry_service_id,
    initiated_by,
    company,
    contact_persons,
    agent_name,
    members,
    services,
    terms_and_conditions,
    notes,
    next_follow_up_date,
    follow_up_remarks,
    uid_number,
    reference_number,
    tags,
  } = formData;

  const customer_details = buildCustomerDetails(
    formData,
    contact_persons,
    company,
  );

  // Transform assigned members
  const assigned_members =
    members?.map((member: any) => ({
      user_id: member.id,
      username: member.name,
      first_name: member.first_name ?? member.name,
      last_name: member.last_name ?? "",
    })) || [];

  // Transform services to quotation_service_items (services are already transformed in the form)
  const quotation_service_items =
    services?.map((service: any) => {
      const unitPrice = parseFloat(
        service.service_amount || service.unit_price || "0",
      );
      // Get quantity from any of the possible field names, default to 1 only if all are null/undefined
      const quantityValue =
        service.product_quantity ?? service.quantity ?? service.productQuantity;
      let quantity = 1;
      if (
        quantityValue != null &&
        quantityValue !== "" &&
        quantityValue !== 0
      ) {
        const parsed =
          typeof quantityValue === "string"
            ? parseFloat(quantityValue)
            : Number(quantityValue);
        if (!isNaN(parsed) && parsed > 0) {
          quantity = parsed; // Preserve decimal quantities for accurate price calculations
        }
      }
      const discountType = service.discount_type || "currency";
      // IMPORTANT: discount_value must be the RAW input value (not calculated)
      // Backend expects raw value: 10 for 10% or 500 for ₹500
      // calculated_discount_value is frontend-only and should NEVER be sent to backend
      // Safeguard: Explicitly use discount_value (raw) and NEVER calculated_discount_value
      const discountValue = (() => {
        // Always use the raw discount_value from the form state
        // NEVER use calculated_discount_value - it's frontend-only
        const rawValue = parseFloat(service.discount_value || "0");
        const calculatedValue = parseFloat(
          service.calculated_discount_value || "0",
        );

        // Safety check: If discount_value appears to be the calculated value, log warning
        if (calculatedValue > 0) {
          if (
            discountType === "currency" &&
            Math.abs(rawValue - calculatedValue) < 0.01
          ) {
            console.warn(
              `[DISCOUNT SAFEGUARD] Service "${service.serviceName || service.product}": discount_value (${rawValue}) matches calculated_discount_value (${calculatedValue}). This may indicate discount_value contains calculated value instead of raw input.`,
            );
          }
          // For percentage: if raw > 100, it's definitely wrong
          if (discountType === "percentage" && rawValue > 100) {
            console.warn(
              `[DISCOUNT SAFEGUARD] Service "${service.serviceName || service.product}": discount_value (${rawValue}) is > 100 for percentage type. This is invalid.`,
            );
          }
        }

        // Always return the raw value (even if it seems wrong, we can't fix it here)
        return rawValue;
      })();
      const gstPercent = parseFloat(service.gst_percentage || "0");

      // Helper function to round to 2 decimal places
      const roundToTwoDecimals = (num: number): number =>
        Math.round(num * 100) / 100;

      // Calculate totals with proper decimal handling
      const productTotal = roundToTwoDecimals(
        Number(unitPrice) * Number(quantity),
      );

      // Calculate actual discount amount from raw discount_value
      // discountValue is raw: 10 for 10% or 500 for ₹500
      let actualDiscountAmount = 0;
      if (
        discountType === "percentage" ||
        discountType === "Percentage" ||
        discountType === "%"
      ) {
        actualDiscountAmount = roundToTwoDecimals(
          (productTotal * discountValue) / 100,
        );
      } else {
        // Currency discount - use raw value directly
        actualDiscountAmount = roundToTwoDecimals(
          Math.min(discountValue, productTotal),
        );
      }

      const productNetTotal = roundToTwoDecimals(
        Math.max(0, productTotal - actualDiscountAmount),
      ); // Ensure not negative
      const gstValue = roundToTwoDecimals((productNetTotal * gstPercent) / 100);
      const finalTotal = roundToTwoDecimals(productNetTotal + gstValue); // Net amount + GST

      // Transform shipping address to proper format
      let shippingAddress = null;
      if (service.serviceAddress) {
        // If it's an object (full address object), use it directly
        if (
          typeof service.serviceAddress === "object" &&
          !Array.isArray(service.serviceAddress)
        ) {
          // Check if it's a complete address object with shipping_address_id
          if (service.serviceAddress.shipping_address_id) {
            // It's a complete object - use it directly, but ensure all fields are present
            shippingAddress = {
              shipping_address_id: service.serviceAddress.shipping_address_id,
              company_id:
                service.serviceAddress.company_id ||
                customer_details?.company_id,
              address: service.serviceAddress.address,
              city: service.serviceAddress.city,
              state: service.serviceAddress.state,
              country: service.serviceAddress.country || "India",
              pincode:
                service.serviceAddress.pincode ||
                service.serviceAddress.pin_code,
              phone_number: service.serviceAddress.phone_number || "",
              phone_country: service.serviceAddress.phone_country || "+91",
              is_default: service.serviceAddress.is_default || false,
              created_at: service.serviceAddress.created_at,
              updated_at: service.serviceAddress.updated_at,
            };
          } else if (
            service.serviceAddress.address &&
            customer_details?.shipping_addresses?.length > 0
          ) {
            // Partial object (only has address field) - try to find complete one from customer_details
            const matchingAddr = customer_details.shipping_addresses.find(
              (addr: any) => {
                // Try to match by address string
                const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                return (
                  addrString === service.serviceAddress.address ||
                  addr.address === service.serviceAddress.address ||
                  (service.serviceAddress.shipping_address_id &&
                    addr.shipping_address_id ===
                      service.serviceAddress.shipping_address_id)
                );
              },
            );
            if (matchingAddr) {
              shippingAddress = matchingAddr;
            } else {
              // Couldn't find match - use partial object (shouldn't happen in normal flow)
              console.warn(
                "Could not find complete shipping address for:",
                service.serviceAddress,
              );
              shippingAddress = service.serviceAddress;
            }
          } else {
            // No customer_details or no match - use the object as-is (shouldn't happen)
            console.warn(
              "Incomplete shipping address object:",
              service.serviceAddress,
            );
            shippingAddress = service.serviceAddress;
          }
        }
        // If it's an array, check if elements are objects or strings
        else if (Array.isArray(service.serviceAddress)) {
          // If array has objects, use the first one; if strings, convert to object
          if (service.serviceAddress.length > 0) {
            const firstAddr = service.serviceAddress[0];
            if (
              typeof firstAddr === "object" &&
              firstAddr.shipping_address_id
            ) {
              shippingAddress = firstAddr;
            } else if (typeof firstAddr === "string") {
              // Try to find matching address from customer_details
              if (customer_details?.shipping_addresses?.length > 0) {
                const matchingAddr = customer_details.shipping_addresses.find(
                  (addr: any) => addr.address === firstAddr,
                );
                shippingAddress = matchingAddr || { address: firstAddr };
              } else {
                shippingAddress = { address: firstAddr };
              }
            }
          }
        }
        // If it's a string (legacy format), try to find matching address from customer_details
        else if (typeof service.serviceAddress === "string") {
          if (customer_details?.shipping_addresses?.length > 0) {
            const matchingAddr = customer_details.shipping_addresses.find(
              (addr: any) => {
                const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                return (
                  addrString === service.serviceAddress ||
                  addr.address === service.serviceAddress
                );
              },
            );
            shippingAddress = matchingAddr || {
              address: service.serviceAddress,
            };
          } else {
            shippingAddress = { address: service.serviceAddress };
          }
        }
      }
      // Fallback to customer's shipping addresses if no address provided
      else if (customer_details?.shipping_addresses?.length > 0) {
        shippingAddress = customer_details.shipping_addresses[0]; // Use first one as object
      }

      const serviceItem = {
        ...(service.quotation_service_item_id
          ? { quotation_service_item_id: service.quotation_service_item_id }
          : {}),
        ...(service.id ? { id: service.id } : {}),
        product_id: service.product_id || service.id,
        product_name: service.serviceName || service.product,
        category_name: service.category_name || service.category || "",
        sub_category_name: service.sub_category_name || "",
        product_type: Array.isArray(service.product_type)
          ? service.product_type
          : service.product_type
            ? [service.product_type]
            : [],
        make_name: service.make_name || "",
        model_name: service.model || service.model_name || "",
        unit_name: service.unit_name || "",
        unit_price: unitPrice,
        hsn_code: service.hsn_code || "",
        sac_code: service.sac_code || service.sac || "",
        gst_percentage: gstPercent,
        gst_value: gstValue,
        remark: service.remark || "",
        serial_number: service.serial_number || service.serial_no || service.serialNo || "",
        product_sku: service.product_sku || service.sku || "",
        product_quantity: quantity,
        gross_value: productTotal, // Use schema field name
        total_service_net_amount: productNetTotal, // Use schema field name
        service_category:
          service.serviceCategory || service.service_category || "",
        service_method:
          (service.serviceMethod || service.service_method || "").trim() ||
          "NA",
        service_type:
          (service.serviceType || service.service_type || "").trim() || "NA",
        service_price: unitPrice,
        discount_type: discountType,
        discount_value: discountValue,
        service_rate: unitPrice,
        total_service_amount: isNaN(finalTotal) ? 0 : finalTotal, // Always use calculated final total (net amount + GST)
        execution_mode: service.executionMode || service.execution_mode || "",
        reported_complaints:
          service.reportedComplaint ||
          service.reported_complaint ||
          service.reported_complaints ||
          "",
        shipping_address: shippingAddress,
        serviceable: true, // Services are serviceable
        is_active: true,
        is_deleted: false,
        prod_description: service.prod_description ?? service.description ?? "",
        additional_details: service.additional_details ?? "",
        accessories: Array.isArray(service.accessories)
          ? service.accessories.map((accessory: any) => ({
              ...mapAccessoryToApiPayload(accessory),
              prod_description: accessory.prod_description,
              additional_details: accessory.additional_details,
              discount_amount: accessory.discount_amount,
              taxable_amount: accessory.taxable_amount,
              gst_value: accessory.gst_value,
              total_amount: accessory.total_amount,
              gross_value: accessory.gross_value,
            }))
          : [],
      };

      return serviceItem;
    }) || [];

  // Transform terms and conditions
  const terms_and_conditions_transformed =
    terms_and_conditions?.map((term: any) => ({
      type: term.type,
      options: term.description || term.option,
    })) || [];

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (num: number): number =>
    Math.round(num * 100) / 100;

  // Calculate totals from services with proper decimal handling
  const sub_total_before_discount = roundToTwoDecimals(
    services?.reduce((sum: number, service: any) => {
      const unitPrice = parseFloat(
        service.service_amount || service.unit_price || "0",
      );
      const qtyValue =
        service.product_quantity ?? service.quantity ?? service.productQuantity;
      let quantity = 1;
      if (qtyValue != null && qtyValue !== "" && qtyValue !== 0) {
        const parsed =
          typeof qtyValue === "string"
            ? parseFloat(qtyValue)
            : Number(qtyValue);
        if (!isNaN(parsed) && parsed > 0) {
          quantity = parsed; // Preserve decimal quantities for accurate calculations
        }
      }
      return sum + unitPrice * quantity;
    }, 0) || 0,
  );

  // Calculate actual discount amount from raw discount_value
  // discountValue is raw: 10 for 10% or 500 for ₹500
  // let actualDiscountAmount = 0;
  // if (
  //   discountType === "percentage" ||
  //   discountType === "Percentage" ||
  //   discountType === "%"
  // ) {
  //   actualDiscountAmount = roundToTwoDecimals(
  //     (productTotal * discountValue) / 100,
  //   );
  // } else {
  //   // Currency discount - use raw value directly
  //   actualDiscountAmount = roundToTwoDecimals(
  //     Math.min(discountValue, productTotal),
  //   );
  // }

  // Calculate total discount value
  const total_discount_value = roundToTwoDecimals(
    services?.reduce((sum: number, service: any) => {
      let actualDiscount = 0;
      if (
        service.discount_type === "percentage" ||
        service.discount_type === "Percentage" ||
        service.discount_type === "%"
      ) {
        actualDiscount = roundToTwoDecimals(
          (parseFloat(service.gross_value.toString() || "0") *
            parseFloat(service.discount_value || "0")) /
            100,
        );
      } else {
        return actualDiscount = roundToTwoDecimals(
          parseFloat(service.discount_value || "0"),
        );
      }
      return sum + actualDiscount;
    }, 0) || 0,
  );

  
  const total_gst_value = roundToTwoDecimals(
    services?.reduce(
      (sum: number, service: any) => sum + parseFloat(service.gst_value || "0"),
      0,
    ) || 0,
  );
  const total_net_value = roundToTwoDecimals(
    services?.reduce(
      (sum: number, service: any) =>
        sum + parseFloat(service.net_value || service.service_amount || "0"),
      0,
    ) || 0,
  );
  const grand_total_amount = roundToTwoDecimals(
    services?.reduce(
      (sum: number, service: any) =>
        sum + parseFloat(service.total || service.service_amount || "0"),
      0,
    ) || 0,
  );

  return {
    bill_from: normalizeBillFrom(formData.bill_from_address),
    quotation_date: convertToUTC(quotation_date) || undefined,
    valid_until_date: convertToUTC(valid_until) || undefined,
    initiated_by,
    enquiry_service_id:
      enquiry_service_id && enquiry_service_id.trim()
        ? enquiry_service_id
        : undefined,
    enquiry_number:
      enquiry_reference_number && enquiry_reference_number.trim()
        ? enquiry_reference_number
        : undefined,
    uid_number: uid_number && uid_number.trim() ? uid_number : undefined,
    reference_number: reference_number && reference_number.trim() ? reference_number : undefined,
    quotation_stage: quotationStage,
    agent_name: agent_name || undefined,
    quotation_status: quotationStatus,
    customer_details,
    assigned_members,
    assigned_technician: (formData.assigned_technician || []).map((t: any) => ({
      user_id: t.user_id || t.id,
      first_name: t.first_name || "",
      last_name: t.last_name || "",
      username: t.username || "",
    })),
    // Default INR currency values for services
    currency_name: "Indian Rupee",
    currency_code: "INR",
    currency_symbol: "₹",
    currency_rate_in_inr: 1,
    notes: notes || undefined,
    // attachments are handled separately as files in FormData
    terms_and_conditions: terms_and_conditions_transformed,
    next_follow_up_date: convertToUTC(next_follow_up_date) || undefined,
    follow_up_remarks: follow_up_remarks || undefined,
    sub_total_before_discount,
    total_discount_value,
    total_gst_value,
    total_net_value,
    grand_total_amount,
    quotation_service_items,
    tags: tags || [],
  };
};

/**
 * Thunk to create a new Service Quotation with file upload support.
 * @param payload - Object containing form data and quotation status/stage options.
 */
export const createServiceQuotation = createAsyncThunk(
  "quotation/createServiceQuotation",
  async (
    {
      formData,
      quotationStatus = "Prepared",
      quotationStage = "Draft",
    }: {
      formData: any;
      quotationStatus?: string;
      quotationStage?: string;
    },
    { rejectWithValue },
  ) => {
    console.log("payload in create service thunk : ", {
      formData,
      quotationStatus,
      quotationStage,
    });

    try {
      // Transform the frontend data to backend format
      const transformedData = transformServiceQuotationData(
        formData,
        quotationStatus,
        quotationStage,
      );

      // Create FormData for multipart upload
      const formDataPayload = new FormData();

      // Add all non-file data as JSON string
      Object.keys(transformedData).forEach((key) => {
        const value = (transformedData as any)[key];
        if (value !== undefined && value !== null) {
          if (typeof value === "object" && !(value instanceof Date)) {
            formDataPayload.append(key, JSON.stringify(value));
          } else if (value instanceof Date) {
            formDataPayload.append(key, value.toISOString());
          } else {
            formDataPayload.append(key, String(value));
          }
        }
      });

      // Add attachments. Existing enquiry URLs are sent as text fields so create can preserve them.
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((item: File | string) => {
          if (item instanceof File) {
            formDataPayload.append("attachments", item);
          } else if (typeof item === "string" && item.trim()) {
            formDataPayload.append("attachments", item);
          }
        });
      }

      const response = await OrderAndSalesAxios.post(
        API_ENDPOINT.QUOTATION.SERVICE.CREATE,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Thunk to update an existing Service Quotation with file upload support.
 * @param data - Object containing { id, quotationStatus, quotationStage, ...payload }
 */
export const updateServiceQuotation = createAsyncThunk(
  "quotation/updateServiceQuotation",
  async (
    {
      id,
      quotationStatus = "Prepared",
      quotationStage = "Draft",
      ...payload
    }: {
      id: string;
      quotationStatus?: string;
      quotationStage?: string;
      [key: string]: any;
    },
    { rejectWithValue },
  ) => {
    try {
      // Transform the frontend data to backend format (same as create)
      const transformedData = transformServiceQuotationData(
        payload,
        quotationStatus,
        quotationStage,
      );

      // Create FormData for multipart upload (consistent with create)
      const formDataPayload = new FormData();

      // Add all non-file data as JSON string
      Object.keys(transformedData).forEach((key) => {
        const value = (transformedData as any)[key];
        if (value !== undefined && value !== null) {
          if (typeof value === "object" && !(value instanceof Date)) {
            formDataPayload.append(key, JSON.stringify(value));
          } else if (value instanceof Date) {
            formDataPayload.append(key, value.toISOString());
          } else {
            formDataPayload.append(key, String(value));
          }
        }
      });

      // Add attachments (files) to FormData if present
      // Separate new files from existing URLs
      if (payload.attachments && payload.attachments.length > 0) {
        const newFiles: File[] = [];
        const existingUrls: string[] = [];

        payload.attachments.forEach((item: File | string) => {
          if (item instanceof File) {
            newFiles.push(item);
          } else if (typeof item === "string") {
            existingUrls.push(item);
          }
        });

        // Append new files to FormData
        newFiles.forEach((file: File) => {
          formDataPayload.append("attachments", file);
        });

        // Send existing URLs as a separate field for backend to keep them
        if (existingUrls.length > 0) {
          formDataPayload.append(
            "existing_attachments",
            JSON.stringify(existingUrls),
          );
        }
      }

      if (payload.version_notes) {
        formDataPayload.append("version_notes", payload.version_notes);
      }

      const response = await OrderAndSalesAxios.put(
        API_ENDPOINT.QUOTATION.SERVICE.UPDATE(id),
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Thunk to fetch the list of all Service Quotations.
 */
export const fetchAllServiceQuotations = createAsyncThunk(
  "quotation/fetchAllServiceQuotations",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    search_type,
    filters,
    type,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    search_type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    type?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    params.append("quotation_stage", type);
    if (search) params.append("search", search);
    if (search_type) params.append("search_type", search_type);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }
    const filterPayload = FilterBuilder(filters ?? []);

    const body = {
      filters: filterPayload,
      ...(search_type ? { search_type } : {}),
    };

    const response: any = await OrderAndSalesAxios.post(
      `${API_ENDPOINT.QUOTATION.SERVICE.LIST}?${params.toString()}`,
      body,
    );
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  },
);

export const fetchServiceQuotationDynamicDropdown = createAsyncThunk(
  "quotaion/fetchServiceQuotationDynamicDropdown",
  async (type: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.get(
        API_ENDPOINT.QUOTATION.SERVICE.DROPDOWN,
      );
      const rawData = response.data?.data || response.data || [];
      const normalizedData = Array.isArray(rawData)
        ? rawData.map((item: any) => ({
            ...item,
            id: item.id || item.quotation_service_id,
            service_quotation_name:
              item.service_quotation_name || item.quotation_number || "",
          }))
        : [];

      return {
        ...response.data,
        data:
          type === "quotation_number"
            ? normalizedData
            : response.data?.data || normalizedData,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry dropdown",
      );
    }
  },
);

export const handleServiceQuotationStatus = createAsyncThunk(
  "quotation/handleServiceQuotationStatus",
  async (
    {
      id,
      quotation_status,
      quotation_stage,
      remark,
    }: {
      id: string;
      quotation_status?: string;
      quotation_stage?: string;
      remark?: string;
    },
    { rejectWithValue },
  ) => {
    const payload: any = { id };
    if (quotation_status) payload.quotation_status = quotation_status;
    if (quotation_stage) payload.quotation_stage = quotation_stage;
    if (remark) payload.remark = remark;

    try {
      const response = await OrderAndSalesAxios.patch(
        API_ENDPOINT.QUOTATION.SERVICE.STATUS_UPDATE(id),
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Thunk to delete a Service Quotation by its ID.
 * @param id - The UUID of the quotation to delete.
 */
export const deleteServiceQuotation = createAsyncThunk(
  "quotation/deleteServiceQuotation",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.delete(
        API_ENDPOINT.QUOTATION.SERVICE.DELETE(id),
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Thunk to export Service Quotations to CSV.
 * @param params - Object containing search, filters, and quotation_stage (type)
 */
export const exportServiceQuotationCSV = async ({
  search,
  filters,
  type, // quotation_stage: "Draft" | "log"
}: {
  search?: string;
  filters?: any;
  type?: string;
}) => {
  const params = new URLSearchParams();
  params.append("export", "csv");
  if (type) params.append("quotation_stage", type);
  if (search) params.append("search", search);

  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await OrderAndSalesAxios.post(
    `${API_ENDPOINT.QUOTATION.SERVICE.LIST}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "quotation_services.csv",
  };
};
